import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Typography,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { GridLegacy as Grid } from "@mui/material";
import { GetApiMethod, PatchApiMethod } from "../../Utils/ApiService";
import "../../Components/Common/Styles/Formstyles.css";
import { useNavigate } from "react-router-dom";
import BoxHeader from "../../Components/Common/Box/Boxheader";
import CameraIcon from "../../Assets/images/svg/Icons/camera-logo.svg";
import Block from "../../Assets/images/svg/Icons/ban-svgrepo-com.svg";
import Edit from "../../Assets/images/svg/Icons/edit-3-svgrepo-com.svg";
import { useSuccessDialog } from "../../Components/Common/SuccesMessages/SuccessDialog";

interface BookingsProps {
  onBack: () => void;
  onEdit: (tab: number, providerData: any) => void; // Not used here, but kept for interface
  providerId: string;
}

const Bookings: React.FC<BookingsProps> = ({ onBack, providerId }) => {
  // const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [provider, setProvider] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const { showSuccess } = useSuccessDialog();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [portfolio, setPortfolio] = useState<{ photos: string[]; videos: string[] }>({
    photos: [],
    videos: [],
  });
  const fetchProvider = useCallback(async () => {
    setLoading(true);
    try {
      const response = await GetApiMethod(`provider/${providerId}`);
      const providerData = response.data?.data || {};

      const services: string[] = [];
      const addons: string[] = [];

      providerData.providerServices?.forEach((item: any) => {
        const name = item.serviceDetail?.name?.trim();
        const detailType = item.serviceDetail?.type?.toLowerCase();
        const providerType = item.type?.toLowerCase();
        if (!name) return;

        const finalType = providerType || detailType;
        if (finalType === "services") services.push(name);
        else if (finalType === "addons") addons.push(name);
      });

      setProvider({
        ...providerData,
        services,
        addons,
        photos: providerData.photos || [],
        videos: providerData.videos || [],
        reviews: providerData.reviews || [],
      });
    } catch (error) {
      console.error("Failed to fetch provider:", error);
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    fetchProvider();
  }, [fetchProvider]);

  const categories = useMemo(() => {
    if (!provider) return "N/A";
    return Array.isArray(provider.categoriesDetail)
      ? provider.categoriesDetail.map((cat: any) => cat.name).join(", ")
      : "N/A";
  }, [provider]);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setActiveTab(newValue);
    },
    []
  );
  const fetchPortfolio = useCallback(async () => {
    try {
      const response = await GetApiMethod(`provider-portfolio/list?id=${providerId}`);
      const records = response?.data?.data?.records || [];

      const photos: string[] = [];
      const videos: string[] = [];

      records.forEach((item: any) => {
        const cleanedUrl = item.mediaUrl?.replace(/\\/g, "/"); // Fix backslashes
        if (!cleanedUrl) return;

        if (item.mediaType === "image") photos.push(cleanedUrl);
        else if (item.mediaType === "video") videos.push(cleanedUrl);
      });

      setPortfolio({ photos, videos });
    } catch (error) {
      console.error("Failed to fetch provider portfolio:", error);
    }
  }, [providerId]);
  useEffect(() => {
    fetchProvider();
    fetchPortfolio(); // ← fetch images/videos separately
  }, [fetchProvider, fetchPortfolio]);
  const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/";

  const normalizeUrl = (url: string) => {
    const cleaned = url.replace(/^\/?/, "").replace(/\\/g, "/");
    return url.startsWith("http") ? url : `${BASE_URL}${cleaned}`;
  };
  const handleEdit = useCallback(() => {
    if (!provider) return;

    const fullProviderData = {
      ...provider,
      photos: (portfolio.photos || []).map(normalizeUrl),
      videos: (portfolio.videos || []).map(normalizeUrl),
      services: provider.providerServices
        ?.filter((item: any) => item.type?.toLowerCase() === "services")
        .map((item: any) => ({
          id: item.serviceDetail?.id || item.serviceId,
          name: item.serviceDetail?.name || "",
          cost: item.cost || "",
          type: item.type || "Services",
        })) || [],
      addons: provider.providerServices
        ?.filter((item: any) => item.type?.toLowerCase() === "addons")
        .map((item: any) => ({
          id: item.serviceDetail?.id || item.serviceId,
          name: item.serviceDetail?.name || "",
          cost: item.cost || "",
          type: item.type || "Addons",
        })) || [],
    };

    navigate(`/providers/${provider.id}/edit`, {
      state: { provider: fullProviderData },
    });
  }, [navigate, provider, portfolio]);

  const blockProvider = useCallback(async () => {
    if (!provider) return;

    try {
      const newStatus = provider.status ? 0 : 1;

      const payload = {
        ...provider,
        status: newStatus,
        categories: provider.categoriesDetail?.map((cat: any) => cat.id) || [],
      };

      await PatchApiMethod(`provider/${provider.id}`, payload);

      showSuccess(
        `Provider "${provider.providerName}" ${newStatus ? "enabled" : "disabled"
        } successfully!`
      );

      // Optionally wait for a short moment before navigating
      setTimeout(() => {
        navigate('/providers');
      }, 1000); // Wait for 1 second to let the toast/dialog appear

    } catch (error) {
      console.error("Error toggling provider status:", error);
    }
  }, [provider, navigate, showSuccess]);

  if (loading) {
    return (
      <Box textAlign="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (!provider) {
    return (
      <Box textAlign="center" mt={5}>
        <Typography variant="h6">Provider not found.</Typography>
      </Box>
    );
  }


  return (
    <Grid container spacing={2} sx={{ padding: isMobile ? 1 : 3 }}>
      {/* Header */}
      <Grid item xs={12}>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          gap={0}
        >
          <BoxHeader
            dashboard_name="Providers"
            image={CameraIcon}
            button_show={true}
            onAdd={() => navigate("/providers/add")}
            tabs={["All Providers"]}
            activeTab={tab}
            onTabChange={(newTab) => setTab(newTab)}
          />

          <Box display="flex" flexDirection="row" gap={1} mb={4}>
            <Button
              variant="contained"
              sx={{ background: "#D44C59" }}
              onClick={() => setDialogOpen(true)}
            >
              <img src={Block} alt="blockimage" style={{ marginRight: 4 }} />
              {provider.status ? "Block Provider" : "Unblock Provider"}
            </Button>

            <Button variant="contained" className="btn-primary" onClick={handleEdit}>
              <img src={Edit} alt="editimage" style={{ marginRight: 4 }} />
              Edit Info
            </Button>
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Typography
          sx={{
            fontSize: isMobile ? 18 : isTablet ? 20 : 24,
            fontFamily: "Poppins, Bold",
          }}
        >
          Provider Details
        </Typography>
      </Grid>

      {/* Tabs */}
      <Grid item xs={12}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : undefined}
        >
          <Tab label="Basic Info" className="app-tab-label" />
          <Tab label="Services" className="app-tab-label" />
          <Tab label="Addons" className="app-tab-label" />
          <Tab label="Photos" className="app-tab-label" />
          <Tab label="Videos" className="app-tab-label" />
          <Tab label="Reviews" className="app-tab-label" />
        </Tabs>
      </Grid>

      {/* Tab Content */}
      <Grid item xs={12}>
        {activeTab === 0 && (
          <Table size={isMobile ? "small" : "medium"}>
            <TableBody>
              <TableRow>
                <TableCell className="table-cell">Provider Name</TableCell>
                <TableCell className="table-cell">{provider.providerName || "-"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="table-cell">Contact Person</TableCell>
                <TableCell className="table-cell">{provider.contactPerson || "-"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="table-cell">Email ID</TableCell>
                <TableCell className="table-cell">{provider.email || "-"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="table-cell">Phone Number</TableCell>
                <TableCell className="table-cell">{provider.phone || "-"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="table-cell">Categories Offered</TableCell>
                <TableCell className="table-cell">{categories}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="table-cell">About Company</TableCell>
                <TableCell className="table-cell">{provider.aboutCompany || "-"}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}

        {activeTab === 1 && (
          <Table size={isMobile ? "small" : "medium"}>
            <TableBody>
              {provider.services.length ? (
                provider.services.map((service: string, index: number) => (
                  <TableRow key={index}>
                    <TableCell className="table-cell">{service}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell className="table-cell">No Services Available</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        {activeTab === 2 && (
          <Table size={isMobile ? "small" : "medium"}>
            <TableBody>
              {provider.addons.length ? (
                provider.addons.map((addon: string, index: number) => (
                  <TableRow key={index}>
                    <TableCell className="table-cell">{addon}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell className="table-cell">No Addons Available</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        {activeTab === 3 && (
          <Box display="flex" flexWrap="wrap" gap={2}>
            {portfolio.photos.length ? (
              portfolio.photos.map((photo: string, idx: number) => (
                <img
                  key={idx}
                  src={photo}
                  alt={`photo-${idx}`}
                  style={{ width: 100, height: 100, objectFit: "cover" }}
                />
              ))
            ) : (
              <Typography>No Photos</Typography>
            )}
          </Box>
        )}

        {activeTab === 4 && (
          <Box display="flex" flexWrap="wrap" gap={2}>
            {portfolio.videos.length ? (
              portfolio.videos.map((video: string, idx: number) => (
                <video
                  key={idx}
                  width={160}
                  height={120}
                  controls
                  src={video}
                ></video>
              ))
            ) : (
              <Typography>No Videos</Typography>
            )}
          </Box>
        )}

        {activeTab === 5 && (
          <Box>
            {provider.reviews.length ? (
              provider.reviews.map((review: string, idx: number) => (
                <Typography key={idx} sx={{ mb: 1 }}>
                  {review}
                </Typography>
              ))
            ) : (
              <Typography>No Reviews</Typography>
            )}
          </Box>
        )}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          fullWidth
          maxWidth="xs"
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 2,
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 600,
              fontSize: 20,
              textAlign: "center",
              pb: 0,
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {provider.status ? "Confirm Block" : "Confirm Unblock"}
          </DialogTitle>

          <DialogContent sx={{ textAlign: "center", mt: 1 }}>
            <Typography sx={{ fontSize: 16, fontFamily: "Open Sans, sans-serif" }}>
              Are you sure you want to {provider.status ? "block" : "unblock"} this provider?
            </Typography>
          </DialogContent>

          <DialogActions
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              mt: 2,
              pb: 2,
            }}
          >
            <Button
              onClick={() => setDialogOpen(false)}
              variant="outlined"
              color="primary"
              sx={{
                minWidth: 100,
                fontWeight: 600,
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              NO
            </Button>

            <Button
              onClick={() => {
                setDialogOpen(false); // Close dialog first
                blockProvider();      // Then proceed to block/unblock
              }}
              variant="contained"
              color={provider.status ? "error" : "success"}
              sx={{
                minWidth: 100,
                fontWeight: 600,
                borderRadius: 2,
                textTransform: "none",
              }}
            >
              YES
            </Button>
          </DialogActions>
        </Dialog>

      </Grid>
    </Grid>
  );
};

export default Bookings;
